import { GridWallAccessEnum } from 'api/enums';
import { CctvCameraGridDataModel } from 'api/models';
import clsx from 'clsx';
import { NoImageIcon } from 'library/icons';
import OverlayLoader from 'modules/dashboard/components/ui/OverlayLoader';
import { CctvPlayer } from 'modules/dashboard/features/camera-cctv';
import CctvStatusBadge from 'modules/dashboard/features/camera-cctv/components/CctvStatusBadge/CctvStatusBadge';
import useCctvStatusChecker from 'modules/dashboard/features/camera-cctv/hooks/useCctvStatusChecker';
import GridWallCctvSneakPeekModal from 'modules/plugins/features/gridwall-detail/components/GridwallCctvSneakPeekModal/GridwallCctvSneakPeekModal';
import useDialog from 'modules/shared/hooks/useDialog';
import { useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

interface IOptions {
    showTimestamp: boolean;
    showCameraName: boolean;
    showProjectName: boolean;
    showCustomText: boolean;
    showZoomed: boolean;
}

interface IGridwallDetailProps {
    grid: CctvCameraGridDataModel;
    options: IOptions;
    access: GridWallAccessEnum;
    isLoading?: boolean;
}

const GridWallLiveCameraCard = (props: IGridwallDetailProps) => {
    const { grid, options, access } = props;

    const { token } = useParams();

    const gridWallSneakPeekDialog = useDialog<{
        grid: CctvCameraGridDataModel;
        options: IOptions;
    }>();

    const accessCode = token ? localStorage.getItem(`accessCode_${token}`) : null;

    const {
        isOnline,
        isOffline,
        isLoading: isLoadingLiveUnit,
        status,
        hlsUrl: streamHlsUrl,
        webrtcUrl: streamWebrtcUrl,
    } = useCctvStatusChecker({
        hlsUrl: grid.hlsUrl,
        webrtcUrl: grid.webrtcUrl,
        gridwall: token ? { token, gridDataId: grid._id, access, accessCode: accessCode ?? undefined } : undefined,
    });

    const marqueeWidth =
        (grid.project?.name?.length ?? 0) + (grid?.cctvCamera?.name?.length ?? 0) + (grid?.customText?.length ?? 0);

    const hasProject = grid.project?.name && grid.project.name.length > 0;
    const hasCamera = grid.cctvCamera?.name && grid.cctvCamera.name.length > 0;

    const hasMessage = grid?.customText && grid.customText.length > 0;

    const shouldShowMessage = options.showCustomText && hasMessage;

    const shouldShowProjectName = options.showProjectName && hasProject;
    const shouldShowCameraName = options.showCameraName && hasCamera;
    const shouldShowBoth = shouldShowProjectName && shouldShowCameraName;

    // Determine if we should show any bottom overlay content
    const hasBottomContent = shouldShowMessage || shouldShowProjectName || shouldShowCameraName;

    return (
        <>
            <div
                className="relative w-full h-full overflow-hidden min-h-0 min-w-0 bg-grayscale-500 cursor-pointer"
                onClick={() => isOnline && gridWallSneakPeekDialog.show({ grid, options })}
            >
                <div className="absolute z-20 left-2 top-2 flex items-center gap-3">
                    {isOffline && (
                        <div className="flex items-center gap-2 h-6 text-white rounded-[4px] text-xs px-2 bg-card-color font-semibold">
                            <CctvStatusBadge status={status} />
                        </div>
                    )}
                    {isOnline && (
                        <div className="flex items-center gap-2 h-6 text-white rounded-[4px] text-xs px-2  bg-danger-600 font-semibold">
                            <span className={['w-1.5 h-1.5 bg-white  rounded-full'].join(' ')}></span>
                            {'LIVE'}
                        </div>
                    )}
                </div>
                {grid?.frameUrl && (
                    <div
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                        style={{
                            backgroundImage: `url(${grid?.frameUrl})`,
                            filter: 'blur(100px)',
                            transform: 'scale(1.1)',
                        }}
                    />
                )}

                <div className="absolute inset-0 flex items-center justify-center">
                    {isOnline && (
                        <CctvPlayer
                            hlsUrl={streamHlsUrl}
                            webrtcUrl={streamWebrtcUrl}
                            className="max-w-full max-h-full object-contain"
                        />
                    )}

                    <OverlayLoader show={isLoadingLiveUnit} />

                    {isOffline && (
                        <>
                            {grid?.frameUrl ? (
                                <img
                                    src={grid?.frameUrl}
                                    alt={'grid'}
                                    className={clsx(
                                        'transition-all duration-300 ease-in-out',
                                        options.showZoomed
                                            ? 'w-full h-full object-cover' // Zoomed: fill entire container
                                            : 'max-w-full max-h-full object-contain' // Not zoomed: maintain aspect ratio
                                    )}
                                />
                            ) : (
                                <div className="absolute inset-0  flex items-center justify-center">
                                    <NoImageIcon />
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Bottom overlay - only show when we have content to display */}
                {hasBottomContent && (
                    <div className="absolute bottom-0 left-0 right-0 z-20">
                        <div className="bg-black bg-opacity-75 px-2 sm:px-4 py-1 sm:py-2">
                            <div className="flex items-center justify-between gap-2 sm:gap-4 h-4 text-xs font-semibold">
                                {/* Text content with marquee for long text */}
                                <div
                                    className={twMerge(
                                        'flex-1 min-w-0 overflow-hidden',
                                        marqueeWidth > 30 ? 'marquee' : ''
                                    )}
                                >
                                    <div className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs">
                                        {shouldShowProjectName && (
                                            <span className="text-white ">{grid.project?.name}</span>
                                        )}

                                        {shouldShowBoth && <span className="text-white flex-shrink-0"> | </span>}

                                        {shouldShowCameraName && (
                                            <span className="text-white ">{grid.cctvCamera?.name}</span>
                                        )}

                                        {shouldShowMessage && (shouldShowProjectName || shouldShowCameraName) && (
                                            <span className="text-white flex-shrink-0"> | </span>
                                        )}

                                        {shouldShowMessage && <span className="text-white">{grid?.customText}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <GridWallCctvSneakPeekModal
                grid={gridWallSneakPeekDialog.data?.grid}
                hlsUrl={streamHlsUrl}
                webrtcUrl={streamWebrtcUrl}
                onClose={gridWallSneakPeekDialog.hide}
                show={gridWallSneakPeekDialog.isOpen}
            />
        </>
    );
};

export default GridWallLiveCameraCard;
